import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type SalaryPaymentStatus = "pending" | "paid";

export type SalaryPaymentRecord = {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string | null;
  salaryId: string | null;
  salaryStatus: string | null;
  periodYear: number;
  periodMonth: number;
  periodLabel: string;
  basicSalary: string;
  bonus: string;
  deductions: string;
  netSalary: string;
  paymentDate: string | null;
  status: SalaryPaymentStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

type PaginatedResponse = {
  data?: Record<string, unknown>[];
  meta?: { current_page?: number; last_page?: number; per_page?: number; total?: number };
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function num(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function mapRow(raw: Record<string, unknown>): SalaryPaymentRecord {
  const st = str(raw.status);
  const status: SalaryPaymentStatus = st === "paid" ? "paid" : "pending";
  return {
    id: str(raw.id),
    employeeId: str(raw.employee_id),
    employeeName: str(raw.employee_name),
    employeeCode: raw.employee_code != null ? str(raw.employee_code) : null,
    salaryId: raw.salary_id != null ? str(raw.salary_id) : null,
    salaryStatus: raw.salary_status != null ? str(raw.salary_status) : null,
    periodYear: num(raw.period_year),
    periodMonth: num(raw.period_month),
    periodLabel: str(raw.period_label),
    basicSalary: str(raw.basic_salary),
    bonus: str(raw.bonus),
    deductions: str(raw.deductions),
    netSalary: str(raw.net_salary),
    paymentDate: raw.payment_date != null ? str(raw.payment_date).slice(0, 10) : null,
    status,
    notes: raw.notes != null ? str(raw.notes) : null,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

export function salaryPaymentsApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export async function listSalaryPayments(params?: {
  page?: number;
  perPage?: number;
  periodYear?: number;
  periodMonth?: number;
  status?: SalaryPaymentStatus | "__all__";
  employeeId?: string;
  search?: string;
}): Promise<{ rows: SalaryPaymentRecord[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>("/api/v1/salary-payments", {
    params: {
      page: params?.page,
      per_page: params?.perPage,
      period_year: params?.periodYear,
      period_month: params?.periodMonth,
      status: params?.status && params.status !== "__all__" ? params.status : undefined,
      employee_id: params?.employeeId,
      search: params?.search?.trim() || undefined,
    },
  });
  const data = response.data?.data ?? [];
  const meta = response.data?.meta ?? {};
  return {
    rows: data.map((r) => mapRow(r as Record<string, unknown>)),
    meta: {
      currentPage: num(meta.current_page) || 1,
      lastPage: num(meta.last_page) || 1,
      perPage: num(meta.per_page) || 20,
      total: num(meta.total),
    },
  };
}

export type SalaryPaymentPreview = {
  salaryId: string;
  employeeId: string;
  periodYear: number;
  periodMonth: number;
  basicSalary: number;
  bonus: number;
  deductions: number;
  netSalary: number;
};

export async function previewSalaryPaymentFromSalary(salaryId: string): Promise<SalaryPaymentPreview> {
  const res = await api.get<{ data: Record<string, unknown> }>(
    `/api/v1/salary-payments/preview-from-salary/${encodeURIComponent(salaryId)}`,
  );
  const d = res.data?.data ?? {};
  return {
    salaryId: str(d.salary_id),
    employeeId: str(d.employee_id),
    periodYear: num(d.period_year),
    periodMonth: num(d.period_month),
    basicSalary: num(d.basic_salary),
    bonus: num(d.bonus),
    deductions: num(d.deductions),
    netSalary: num(d.net_salary),
  };
}

export async function createSalaryPayment(body: {
  employeeId: string;
  periodYear: number;
  periodMonth: number;
  salaryId?: string | null;
  basicSalary?: string;
  bonus?: string;
  deductions?: string;
  netSalary?: string;
  paymentDate?: string | null;
  notes?: string | null;
}): Promise<{ message: string; payment: SalaryPaymentRecord }> {
  const res = await api.post<{ message?: string; data?: Record<string, unknown> }>("/api/v1/salary-payments", {
    employee_id: Number(body.employeeId),
    period_year: body.periodYear,
    period_month: body.periodMonth,
    salary_id: body.salaryId ? Number(body.salaryId) : null,
    basic_salary: body.basicSalary,
    bonus: body.bonus,
    deductions: body.deductions,
    net_salary: body.netSalary,
    payment_date: body.paymentDate || null,
    notes: body.notes ?? null,
  });
  return {
    message: str(res.data?.message),
    payment: mapRow((res.data?.data ?? {}) as Record<string, unknown>),
  };
}

export async function updateSalaryPayment(
  id: string,
  body: Partial<{
    basicSalary: string;
    bonus: string;
    deductions: string;
    netSalary: string;
    paymentDate: string | null;
    notes: string | null;
  }>,
): Promise<{ message: string; payment: SalaryPaymentRecord }> {
  const payload: Record<string, unknown> = {};
  if (body.basicSalary !== undefined) payload.basic_salary = body.basicSalary;
  if (body.bonus !== undefined) payload.bonus = body.bonus;
  if (body.deductions !== undefined) payload.deductions = body.deductions;
  if (body.netSalary !== undefined) payload.net_salary = body.netSalary;
  if (body.paymentDate !== undefined) payload.payment_date = body.paymentDate;
  if (body.notes !== undefined) payload.notes = body.notes;

  const res = await api.patch<{ message?: string; data?: Record<string, unknown> }>(`/api/v1/salary-payments/${encodeURIComponent(id)}`, payload);
  return {
    message: str(res.data?.message),
    payment: mapRow((res.data?.data ?? {}) as Record<string, unknown>),
  };
}

export async function markSalaryPaymentPaid(id: string, paymentDate?: string): Promise<{ message: string; payment: SalaryPaymentRecord }> {
  const res = await api.post<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/salary-payments/${encodeURIComponent(id)}/mark-paid`,
    { payment_date: paymentDate || null },
  );
  return {
    message: str(res.data?.message),
    payment: mapRow((res.data?.data ?? {}) as Record<string, unknown>),
  };
}

export async function deleteSalaryPayment(id: string): Promise<string> {
  const res = await api.delete<{ message?: string }>(`/api/v1/salary-payments/${encodeURIComponent(id)}`);
  return str(res.data?.message);
}
